import {
  Button,
  Card,
  CardBlock,
  CardContent,
  CardHeader,
  CardTitle,
  DescriptionDetail,
  DescriptionList,
  DescriptionTerm,
  Tag,
} from "@diametral/design-system/react";

import { useState } from "react";

import type { ProjectDetail, Stream, StreamMember } from "../../api/types";
import { ProjectStatusTag } from "../../components/Badges";
import { PersonCell, names } from "../../components/PersonCell";
import { PriorityTag, StatusSignal, WeatherSignal } from "../../components/Pilot";
import { PlainText } from "../../components/PlainText";
import { TrlMeter } from "../../components/TrlMeter";
import { formatDate, formatDateTime } from "../../lib/format";
import { LINK_KIND } from "../../lib/labels";
import { t, useTranslation } from "../../lib/i18n";
import { StreamPilotSummary, StreamRolesDialog, TrackingDialog } from "./StreamPilot";

function memberPeriod(member: StreamMember): string {
  const start = formatDate(member.start_date);
  if (member.upcoming) return t("from {{date}}", { date: start });
  if (!member.end_date) return t("since {{date}}", { date: start });
  return t("since {{date}}, until {{end}}", { date: start, end: formatDate(member.end_date) });
}

function StreamCard({
  stream,
  project,
  canManage,
  onEnd,
  myPersonId,
}: {
  stream: Stream;
  project: ProjectDetail;
  canManage: boolean;
  onEnd: (member: StreamMember, stream: Stream) => void;
  myPersonId: number | null;
}) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<"track" | "roles" | null>(null);
  const current = stream.members.filter((m) => !m.upcoming);
  const upcoming = stream.members.filter((m) => m.upcoming);
  const parent = stream.parent_id ? project.streams.find((s) => s.id === stream.parent_id) : null;
  const open = project.status !== "closed" && !stream.archived;
  return (
    <Card className={parent ? "ada-pilot-sub" : undefined}>
      <CardHeader>
        <div className="ada-row">
          <CardTitle>
            {parent ? <span className="ada-faint">{parent.title} ↳ </span> : null}
            {stream.title}
          </CardTitle>
          <span className="ada-cluster">
            {stream.archived ? <Tag>{t("Archived")}</Tag> : null}
            <span className="ada-faint">
              {t("{{count}} members", { count: current.length })}
              {upcoming.length ? ` · ${t("{{number}} upcoming", { number: upcoming.length })}` : ""}
            </span>
            {open && stream.can_track ? (
              <Button size="sm" variant="outline" onClick={() => setDialog("track")}>
                {t("Steer")}
                <span className="sr-only"> {stream.title}</span>
              </Button>
            ) : null}
            {open && canManage ? (
              <Button size="sm" variant="ghost" onClick={() => setDialog("roles")}>
                {t("Roles")}
                <span className="sr-only"> {stream.title}</span>
              </Button>
            ) : null}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="ada-stack ada-stack--tight">
          <StreamPilotSummary stream={stream} />
          {stream.description ? <PlainText text={stream.description} className="ada-muted" /> : null}
          {stream.members.length ? (
            <ul className="ada-list" aria-label={t("Members of stream {{name}}", { name: stream.title })}>
              {stream.members.map((member) => (
                <li key={member.assignment_id} className="ada-row">
                  <span className="ada-cluster">
                    <PersonCell person={member} />
                    {member.id === myPersonId ? <Tag tone="info">{t("You")}</Tag> : null}
                    {member.upcoming ? <Tag tone="warning">{t("Upcoming")}</Tag> : null}
                  </span>
                  <span className="ada-cluster">
                    <span className="ada-faint">{memberPeriod(member)}</span>
                    {canManage ? (
                      <Button size="sm" variant="ghost" onClick={() => onEnd(member, stream)}>
                        {member.upcoming ? t("Cancel") : t("End")}
                        <span className="sr-only"> {t("the assignment of {{name}}", { name: member.name })}</span>
                      </Button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ada-faint">{t("Nobody is assigned to this stream.")}</p>
          )}
        </div>
      </CardContent>
      {dialog === "track" ? <TrackingDialog slug={project.slug} stream={stream} onClose={() => setDialog(null)} /> : null}
      {dialog === "roles" ? <StreamRolesDialog slug={project.slug} stream={stream} onClose={() => setDialog(null)} /> : null}
    </Card>
  );
}

export interface TeamMember {
  person: StreamMember;
  streams: string[];
  roles: Array<"TL" | "PO">;
  upcoming: boolean;
}

/** Everyone on the project's active streams, once, with their streams and TL/PO roles (TL/PO first). */
export function projectTeam(project: ProjectDetail): TeamMember[] {
  const byId = new Map<number, TeamMember>();
  for (const stream of project.streams.filter((s) => !s.archived)) {
    for (const member of stream.members) {
      const entry = byId.get(member.id) ?? { person: member, streams: [], roles: [], upcoming: true };
      entry.streams.push(stream.title);
      entry.upcoming = entry.upcoming && member.upcoming;
      byId.set(member.id, entry);
    }
  }
  for (const entry of byId.values()) {
    if (project.tech_leads.some((p) => p.id === entry.person.id)) entry.roles.push("TL");
    if (project.product_owners.some((p) => p.id === entry.person.id)) entry.roles.push("PO");
  }
  return [...byId.values()].sort(
    (a, b) => b.roles.length - a.roles.length || a.person.name.localeCompare(b.person.name),
  );
}

function TeamCard({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const team = projectTeam(project);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Team")}</CardTitle>
      </CardHeader>
      <CardContent>
        {team.length ? (
          <ul className="ada-list" aria-label={t("Project team")}>
            {team.map((member) => (
              <li key={member.person.id} className="ada-stack ada-stack--tight">
                <span className="ada-cluster">
                  <PersonCell person={member.person} />
                  {member.roles.map((role) => (
                    <Tag key={role} tone="info">
                      {role}
                    </Tag>
                  ))}
                  {member.upcoming ? <Tag tone="warning">{t("Upcoming")}</Tag> : null}
                </span>
                <span className="ada-faint">{member.streams.join(" · ")}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ada-faint">{t("Nobody is assigned to this project yet.")}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function OverviewTab({
  project,
  myPersonId,
  onEndAssignment,
}: {
  project: ProjectDetail;
  myPersonId: number | null;
  onEndAssignment: (member: StreamMember, stream: Stream) => void;
}) {
  const { t } = useTranslation();
  const active = project.streams.filter((s) => !s.archived);
  const archived = project.streams.filter((s) => s.archived);
  const streamTitle = (id: number | null) => project.streams.find((s) => s.id === id)?.title;
  const canManage = project.can_manage && project.status !== "closed";

  return (
    <div className="ada-split">
      <div className="ada-stack ada-stack--loose">
        <section className="ada-stack" aria-labelledby="project-description">
          <h2 id="project-description" className="ada-section-title">
            {t("Overview")}
          </h2>
          <PlainText text={project.description} empty={t("No description yet.")} />
        </section>

        <section className="ada-stack" aria-labelledby="project-streams">
          <h2 id="project-streams" className="ada-section-title">
            {t("Streams and team")}
          </h2>
          <p className="ada-faint">{t("People are assigned to a stream, never to the whole project.")}</p>
          {active.map((stream) => (
            <StreamCard
              key={stream.id}
              stream={stream}
              project={project}
              canManage={canManage}
              onEnd={onEndAssignment}
              myPersonId={myPersonId}
            />
          ))}
          {archived.length ? (
            <details>
              <summary className="ada-muted">{t("{{count}} archived streams", { count: archived.length })}</summary>
              <div className="ada-stack" style={{ marginTop: "var(--ds-space-3)" }}>
                {archived.map((stream) => (
                  <StreamCard
                    key={stream.id}
                    stream={stream}
                    project={project}
                    canManage={canManage}
                    onEnd={onEndAssignment}
                    myPersonId={myPersonId}
                  />
                ))}
              </div>
            </details>
          ) : null}
        </section>
      </div>

      <aside className="ada-stack">
        <Card>
          <CardBlock>
            <DescriptionList>
              <DescriptionTerm>{t("Status")}</DescriptionTerm>
              <DescriptionDetail>
                <ProjectStatusTag status={project.status} />
              </DescriptionDetail>
              <DescriptionTerm>{t("Steering")}</DescriptionTerm>
              <DescriptionDetail>
                <span className="ada-cluster">
                  <PriorityTag priority={project.priority} />
                  <StatusSignal status={project.pilot_status} />
                  <WeatherSignal weather={project.weather} />
                </span>
              </DescriptionDetail>
              <DescriptionTerm>{t("Next committee")}</DescriptionTerm>
              <DescriptionDetail>{project.next_passage ? formatDate(project.next_passage) : <span className="ada-faint">{t("Not planned")}</span>}</DescriptionDetail>
              <DescriptionTerm>{t("PO + TL")}</DescriptionTerm>
              <DescriptionDetail>
                {project.socle.complete ? t("Complete") : <span className="ada-pilot-gap">{t("Incomplete")}</span>}
              </DescriptionDetail>
              <DescriptionTerm>{t("Maturity")}</DescriptionTerm>
              <DescriptionDetail>
                <TrlMeter trl={project.trl} label={project.trl_label} />
              </DescriptionDetail>
              <DescriptionTerm>{t("Tech leads")}</DescriptionTerm>
              <DescriptionDetail>{names(project.tech_leads)}</DescriptionDetail>
              <DescriptionTerm>{t("Product owners")}</DescriptionTerm>
              <DescriptionDetail>{names(project.product_owners)}</DescriptionDetail>
              <DescriptionTerm>{t("Headcount")}</DescriptionTerm>
              <DescriptionDetail>
                <span className="ds-numeric">{t("{{count}} people", { count: project.member_count })}</span>
              </DescriptionDetail>
              <DescriptionTerm>{t("Created on")}</DescriptionTerm>
              <DescriptionDetail>{formatDateTime(project.created_at)}</DescriptionDetail>
              {project.closed_at ? (
                <>
                  <DescriptionTerm>{t("Closed on")}</DescriptionTerm>
                  <DescriptionDetail>{formatDateTime(project.closed_at)}</DescriptionDetail>
                </>
              ) : null}
              {project.can_edit && project.pending_requests ? (
                <>
                  <DescriptionTerm>{t("Pending requests")}</DescriptionTerm>
                  <DescriptionDetail className="ds-numeric">{project.pending_requests}</DescriptionDetail>
                </>
              ) : null}
            </DescriptionList>
          </CardBlock>
        </Card>

        <TeamCard project={project} />

        <Card>
          <CardHeader>
            <CardTitle>{t("Links")}</CardTitle>
          </CardHeader>
          <CardContent>
            {project.links.length ? (
              <ul className="ada-list">
                {project.links.map((link) => (
                  <li key={link.id} className="ada-stack ada-stack--tight">
                    <div className="ada-cluster">
                      <Tag tone="neutral">{LINK_KIND[link.kind] ?? link.kind}</Tag>
                      {link.label ? <strong>{link.label}</strong> : null}
                    </div>
                    {link.url.startsWith("https://") ? (
                      <a className="ada-link ada-mono" href={link.url} target="_blank" rel="noreferrer noopener">
                        {link.url}
                      </a>
                    ) : (
                      <span className="ada-mono">{link.url}</span>
                    )}
                    <span className="ada-faint">{streamTitle(link.stream_id) ?? t("Whole project")}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ada-faint">
                {t("No link yet. Code repositories are used to draft the report from the commits.")}
              </p>
            )}
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
